import { ReactElement, ReactNode, useEffect } from 'react'
import type { NextPage } from 'next'
import type { AppContext, AppProps } from 'next/app'
import App from 'next/app'
import ReactModal from 'react-modal'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { DatadogInit } from '~/components/shared/Scripts'
import Layout from '~/components/shared/Layout'
import { LocalisationProvider } from '~/context/localisation'
import { AuthProvider } from '~/context/auth'
import QueryClientProvider from '~/context/queryClient'
import { PartnerProvider } from '~/context/partner'
import { UserProvider } from '~/context/user'
import { ThemeProvider } from '~/context/theme'
import { FeatureFlagProvider } from '~/context/featureFlag'
import { AccountProvider } from '~/context/account'
import { NextPageError } from '~/server/errors'
import { ErrorBoundary } from '~/components/shared/ErrorBoundary'
import useCorrelationId from '~/hooks/useCorrelationId/useCorrelationId'
import useHotjar from '~/hooks/useHotjar/useHotjar'
import useRemoveExpiredBasketItems from '~/hooks/useRemoveExpiredBasketItems'
import { MaintenanceModePage } from '~/components/shared/MaintenanceModePage'
import { PaymentOptionsProvider } from '~/components/orders/PaymentOptionsContext/PaymentOptionsProvider'

// API
import {
  setPartnerHeader,
  setCorrelationIdHeader,
  setPlatformTypeHeader,
  setApiRouteBaseUrl,
} from '~/services/clients/nextApiClient'

// Schemas
import { MessageCluster, PlatformConfig } from '@reward-platform/shared-schemas'
import { FeatureFlagMap } from '~/schemas/featureFlags'

// Server
import { logger, logRequestToDatadog } from '~/server/logger'
import { getAllFeatureVariablesForUser } from '~/server/devcycle'
import {
  getPlatformConfigFromPageContext,
  handleRedirectToAviosPlatform,
  handleRedirectToSwitchFly,
  handleSilentLogin,
} from '~/server/pageHandlers'
import { getNonceFromRequest } from '~/server/utils/nonce'

import '../styles/global.css'
import '../styles/typography.css'
import '~/styles/chat-widget.css'
import { NotificationProvider } from '~/context/notification'

ReactModal.setAppElement('#__next')

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppServerProps = PlatformConfig & {
  inMaintenanceMode?: boolean
  featureFlags?: FeatureFlagMap
  initialMessages?: MessageCluster
  nonce?: string
}

interface MyAppProps extends AppServerProps, AppProps {
  Component: NextPageWithLayout
}

const DEFAULT_MESSAGE_CLUSTER = [
  'reward-platform', // legacy message formats
  'reward-platform-microcopy',
]
const initialClusters =
  process.env.NEXT_PUBLIC_MESSAGE_CLUSTERS?.split(',') ?? DEFAULT_MESSAGE_CLUSTER

const ClearExpiredBasketItems = () => {
  useRemoveExpiredBasketItems()

  return null
}

const useSetApiClient = ({ opCo, theme, basePath }: PlatformConfig) => {
  const { correlationId, resetCorrelationId } = useCorrelationId()

  // test this is even needed, maybe reckon middleware can add this
  useEffect(() => {
    setPartnerHeader(opCo)
  }, [opCo])

  useEffect(() => {
    setApiRouteBaseUrl(basePath)
  }, [basePath])

  useEffect(() => {
    const platformType = theme === 'avios' ? 'avios' : 'whitelabel'
    setPlatformTypeHeader(platformType)
  }, [theme])

  useEffect(() => {
    if (correlationId == null) {
      resetCorrelationId()
      return
    }
    setCorrelationIdHeader(correlationId)
  }, [correlationId, resetCorrelationId])
}

function MyApp(props: Readonly<MyAppProps>): JSX.Element {
  const {
    Component,
    pageProps,
    opCo,
    theme: platformTheme,
    basePath,
    locale,
    inMaintenanceMode,
    featureFlags,
    initialMessages = {},
    nonce,
  } = props

  useSetApiClient(props)
  useHotjar()

  const getLayout = Component.getLayout ?? ((page) => <Layout variant="search">{page}</Layout>)

  if (inMaintenanceMode) {
    return <MaintenanceModePage themeId={platformTheme} />
  }

  return (
    <ErrorBoundary opCo={opCo} themeId={platformTheme}>
      <PartnerProvider opCo={opCo} basePath={basePath}>
        <ThemeProvider themeId={platformTheme} nonce={nonce}>
          <LocalisationProvider
            locale={locale}
            initialMessages={pageProps.initialMessages ?? initialMessages}
            initialClusterKeys={initialClusters}
          >
            <NotificationProvider>
              <QueryClientProvider>
                <AuthProvider>
                  <FeatureFlagProvider initialFeatureFlags={featureFlags}>
                    <DatadogInit />
                    <ClearExpiredBasketItems />
                    {/* disabled in prod by default */}
                    <ReactQueryDevtools />
                    <UserProvider>
                      <AccountProvider>
                        <PaymentOptionsProvider useStorage>
                          {getLayout(<Component {...pageProps} />)}
                        </PaymentOptionsProvider>
                      </AccountProvider>
                    </UserProvider>
                  </FeatureFlagProvider>
                </AuthProvider>
              </QueryClientProvider>
            </NotificationProvider>
          </LocalisationProvider>
        </ThemeProvider>
      </PartnerProvider>
    </ErrorBoundary>
  )
}

MyApp.getInitialProps = async (appContext: AppContext) => {
  const appProps = await App.getInitialProps(appContext)
  const platformConfig = getPlatformConfigFromPageContext(appContext.ctx)

  const request = appContext.ctx.req
  const response = appContext.ctx.res

  const nonce = getNonceFromRequest(appContext.ctx.req)

  const defaultProps: AppServerProps = { ...appProps, ...platformConfig, nonce }

  const isServerSide = !!response && !!request
  if (isServerSide) {
    try {
      // Fetch feature toggles for user
      const featureFlagVariableMap = await getAllFeatureVariablesForUser(request, response)

      // Fetch local fallback messages from json
      const defaultMessageJson = (await import(`../i18n/defaultMessages/messages.en.json`)).default
      const defaultLegacyMessageJson = (
        await import(`../i18n/defaultMessages/messages.legacy.json`)
      ).default
      const initialMessages = { ...defaultMessageJson, ...defaultLegacyMessageJson }
      defaultProps.initialMessages = initialMessages

      // if feature toggle for maintenance mode is on, return with prop without running rest of function
      const inMaintenanceMode = featureFlagVariableMap['maintenance-mode']?.value
      if (inMaintenanceMode) {
        return { ...defaultProps, inMaintenanceMode }
      }

      // Redirects to avios.com from the whitelabel products if feature toggle on
      const shouldRedirectToAvios =
        !!featureFlagVariableMap['redirect-rewards-to-avios']?.value &&
        platformConfig.theme !== 'avios'
      handleRedirectToAviosPlatform(appContext.ctx, shouldRedirectToAvios)

      if (featureFlagVariableMap['redirect-switchfly-iberia']?.value) {
        handleRedirectToSwitchFly(appContext.ctx, ['IB'])
      }

      handleSilentLogin(appContext.ctx)

      // TODO: improve to include generic error page
      // Handle error response
      if (appContext.ctx.err) {
        const pageError = new NextPageError(appContext.ctx.err)
        logRequestToDatadog(request, response, pageError)
      }

      return { ...defaultProps, featureFlags: featureFlagVariableMap }
    } catch (error) {
      // TODO: improve error handling using improved system
      logger.error('Error in App.getInitialProps', error)
      return { ...defaultProps }
    }
  }

  return { ...defaultProps }
}

export default MyApp
